/**
 * RealTimeProvider - Supabase Realtime Management
 * ================================================
 * Uses Supabase Realtime (Postgres Change subscriptions) for live updates
 * Replaces Socket.IO with native Supabase real-time subscriptions
 */

import React, { createContext, useContext, useEffect, useRef, useState, useCallback } from 'react';
import { RealtimeChannel } from '@supabase/supabase-js';
import { useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { supabase } from '../lib/supabase';
import { isGameLive, getHomeScore, getAwayScore } from '../utils/gameStatus';

// Sport types
type Sport = 'college_football' | 'ncaaf' | 'nfl' | 'ncaab' | 'nba' | 'mlb' | 'nhl';
type UpdateType = 'odds_update' | 'game_update' | 'prediction_update' | 'market_movement' | 'score_update';

// Types
interface RealTimeUpdate {
  type: UpdateType;
  sport?: Sport;
  gameId: string;
  data: any;
  timestamp: string;
}

interface MarketMovement {
  gameId: string;
  type: 'spread' | 'moneyline' | 'total';
  oldValue: number;
  newValue: number;
  direction: 'up' | 'down';
  significant: boolean;
}

interface RealTimeContextType {
  channels: Map<string, RealtimeChannel>;
  isConnected: boolean;
  connectionStatus: 'connecting' | 'connected' | 'disconnected' | 'error';
  subscribe: (channel: string, gameId?: string, sport?: Sport) => void;
  unsubscribe: (channel: string, gameId?: string, sport?: Sport) => void;
  subscribeToSport: (sport: Sport) => void;
  unsubscribeFromSport: (sport: Sport) => void;
  lastUpdate: RealTimeUpdate | null;
  reconnectAttempts: number;
}

// Create context
const RealTimeContext = createContext<RealTimeContextType | undefined>(undefined);

export const useRealTime = () => {
  const context = useContext(RealTimeContext);
  if (!context) {
    throw new Error('useRealTime must be used within RealTimeProvider');
  }
  return context;
};

interface RealTimeProviderProps {
  children: React.ReactNode;
}

// Export Sport type for use in other components
export type { Sport, UpdateType };

export const RealTimeProvider: React.FC<RealTimeProviderProps> = ({ children }) => {
  const queryClient = useQueryClient();
  const channelsRef = useRef<Map<string, RealtimeChannel>>(new Map());
  const [isConnected, setIsConnected] = useState(false);
  const [connectionStatus, setConnectionStatus] = useState<RealTimeContextType['connectionStatus']>('disconnected');
  const [lastUpdate, setLastUpdate] = useState<RealTimeUpdate | null>(null);
  const [reconnectAttempts, setReconnectAttempts] = useState(0);

  // Debounce ref: prevent cascade of refetches from rapid DB changes
  // (odds_data cron writes 100s of rows/min; games table updated every 30s)
  const refetchDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Per-gameId throttle for live_game_state events. MLB fires both inning_half_ended
  // and inning_ended within ~1–6s; absorb the dual hit at the dispatch layer.
  const liveStateLastAppliedRef = useRef<Map<string, number>>(new Map());
  const LIVE_STATE_THROTTLE_MS = 2500;

  // Initialize Supabase Realtime subscriptions
  // NOTE: We subscribe to `games` and `live_game_state` tables.
  // The `odds_data` table (12M+ rows, updated every minute by cron) is intentionally excluded —
  // subscribing to it floods the client with events and crashes mobile Safari.
  // Odds are refreshed via React Query's staleTime polling instead.
  useEffect(() => {
    console.log('[RealTimeProvider] Initializing Supabase Realtime subscriptions (games + live_game_state)');
    setConnectionStatus('connecting');

    // KAN-23253 — per-effect-run nonce on channel topic. supabase-js dedupes
    // channels by topic in client.channels[]; without a nonce, a remount
    // (HMR in dev, StrictMode double-mount, or provider re-mount) that races
    // the async removeChannel() cleanup gets back the existing `joined`
    // channel and `.on('postgres_changes', ...)` throws.
    // (Mirror of mobile-web fix in PR #561 / KAN-23168.)
    const nonce = Math.random().toString(36).slice(2, 10);

    // Create channel for games table changes
    const gamesChannel = supabase.channel(`games-changes-${nonce}`)
      .on(
        'postgres_changes',
        {
          event: '*', // Listen to all events (INSERT, UPDATE, DELETE)
          schema: 'public',
          table: 'games'
        },
        (payload) => {
          handleDatabaseChange('game', payload);
        }
      )
      .subscribe((status) => {
        console.log(`[RealTimeProvider] Games channel status: ${status}`);
        if (status === 'SUBSCRIBED') {
          setIsConnected(true);
          setConnectionStatus('connected');
          setReconnectAttempts(0);
        } else if (status === 'CHANNEL_ERROR') {
          setConnectionStatus('error');
          setReconnectAttempts(prev => prev + 1);
        } else if (status === 'CLOSED') {
          setIsConnected(false);
          setConnectionStatus('disconnected');
        }
      });

    // Create channel for live_game_state changes (webhook-driven live scores)
    // KAN-23253 — same per-effect nonce as games-changes above; reuses `nonce`.
    const liveGameStateChannel = supabase.channel(`live-game-state-changes-${nonce}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'live_game_state'
        },
        (payload) => {
          // CRITICAL FIX: For UPDATE events with default replica identity, payload.new = {}
          // (truthy empty object), so `payload.new || payload.old` yields {} and game_id is
          // undefined — causing an early return with no cache invalidation. Extract from both.
          const newRecord = payload.new as any;
          const oldRecord = payload.old as any;
          const gameId = newRecord?.game_id || oldRecord?.game_id;
          const sport = (newRecord?.sport || oldRecord?.sport) as Sport | undefined;

          if (!gameId) return;

          // Throttle MLB inning_half_ended + inning_ended dual fire.
          const now = Date.now();
          const lastAt = liveStateLastAppliedRef.current.get(gameId) ?? 0;
          if (now - lastAt < LIVE_STATE_THROTTLE_MS) return;
          liveStateLastAppliedRef.current.set(gameId, now);

          // Surgically patch any LiveGameStatesProvider caches that include this gameId.
          // The provider keys with ['live-game-states', sortedJoinedIds]. We don't know the
          // exact slice key here, so iterate matching queries and merge into the right map.
          // Falling back to invalidate would re-issue the parent batch query — we want to avoid that.
          queryClient.setQueriesData({ queryKey: ['live-game-states'] }, (old: any) => {
            if (!old || typeof old !== 'object') return old;
            if (!(gameId in old)) {
              // The slice doesn't include this game — leave the cache untouched.
              return old;
            }
            const prior = old[gameId] ?? { game_id: gameId };
            return { ...old, [gameId]: { ...prior, ...newRecord } };
          });

          // Legacy gameScores query (used by GameDetail.tsx) — keep invalidate.
          if (sport) {
            queryClient.invalidateQueries({ queryKey: ['gameScores', gameId, sport] });
          }
          queryClient.invalidateQueries({ queryKey: ['gameScores', gameId] });
        }
      )
      .subscribe((status) => {
        console.log(`[RealTimeProvider] live_game_state channel status: ${status}`);
      });

    // Store channels in ref
    channelsRef.current.set('games', gamesChannel);
    channelsRef.current.set('live_game_state', liveGameStateChannel);

    // Cleanup
    return () => {
      if (refetchDebounceRef.current) {
        clearTimeout(refetchDebounceRef.current);
      }
      channelsRef.current.forEach((channel, key) => {
        supabase.removeChannel(channel);
        console.log(`[RealTimeProvider] Removed channel: ${key}`);
      });
      channelsRef.current.clear();
      setIsConnected(false);
      setConnectionStatus('disconnected');
    };
  }, []); // Only run once on mount

  // Handle database changes from Supabase Realtime
  const handleDatabaseChange = useCallback((type: 'game' | 'odds', payload: any) => {
    const { eventType, new: newRecord, old: oldRecord } = payload;

    console.log(`[RealTimeProvider] Processing ${type} change:`, { eventType, newRecord, oldRecord });

    if (type === 'game' && (eventType === 'INSERT' || eventType === 'UPDATE')) {
      // CRITICAL FIX: For UPDATE events with default replica identity, the 'id' field
      // is not included in newRecord (only changed fields are sent). Use oldRecord as fallback.
      const enrichedRecord = {
        ...newRecord,
        id: newRecord.id || oldRecord?.id
      };

      // Game update - call existing handler with transformed data
      handleGameUpdate(enrichedRecord, enrichedRecord.sport as Sport);
    } else if (type === 'odds' && (eventType === 'INSERT' || eventType === 'UPDATE')) {
      // Same fix for odds updates
      const enrichedRecord = {
        ...newRecord,
        id: newRecord.id || oldRecord?.id
      };

      // Odds update - call existing handler with transformed data
      handleOddsUpdate(enrichedRecord, enrichedRecord.sport as Sport);
    }
  }, []);

  // Handle odds updates
  const handleOddsUpdate = useCallback((data: any, sport?: Sport) => {
    const update: RealTimeUpdate = {
      type: 'odds_update',
      sport: sport || data.sport,
      gameId: data.id || data.game_id || data.gameId,
      data: data.odds || data,
      timestamp: new Date().toISOString()
    };
    setLastUpdate(update);

    // Invalidate odds query for this game
    if (update.gameId) {
      queryClient.invalidateQueries({ queryKey: ['gameOdds', update.gameId] });
    }

    // Also invalidate sport-specific queries
    if (update.sport) {
      queryClient.invalidateQueries({ queryKey: ['odds', update.sport] });
    }

    // Show notification for significant movements
    if (data.movement?.significant) {
      toast(`Significant odds movement for game ${update.gameId}`, {
        icon: data.movement.direction === 'up' ? '📈' : '📉'
      });
    }
  }, [queryClient]);

  // Handle game updates — patch caches surgically (no network refetch when the
  // updated row already lives in the cached list). Falling back to invalidate caused
  // a full re-fetch every 30s when the games cron updated rows.
  const handleGameUpdate = useCallback((data: any, sport?: Sport) => {
    const update: RealTimeUpdate = {
      type: 'game_update',
      sport: sport || data.sport,
      gameId: data.id || data.game_id || data.gameId,
      data: data,
      timestamp: new Date().toISOString()
    };

    setLastUpdate(update);

    // Invalidate game-specific queries immediately (no network request until mount)
    if (update.gameId) {
      if (update.sport) {
        queryClient.invalidateQueries({ queryKey: ['gameScores', update.gameId, update.sport] });
      }
      queryClient.invalidateQueries({ queryKey: ['gameScores', update.gameId] });
      queryClient.invalidateQueries({ queryKey: ['game', update.gameId] });
    }

    // Surgical patch on the home-games and games-list caches: replace the matching row
    // in place. The cache is List<GameRow> for ['home-games', sportId] and
    // { games: GameRow[] } for ['games', sport, ...]. Iterate matching queries and patch
    // only when the gameId is present. If the row isn't found (e.g., new game inserted),
    // fall back to a debounced invalidate as a safety net.
    let patched = false;
    queryClient.setQueriesData({ queryKey: ['home-games'] }, (old: any) => {
      if (!Array.isArray(old)) return old;
      const idx = old.findIndex((g: any) => g?.gameId === update.gameId || g?.id === update.gameId);
      if (idx === -1) return old;
      const next = old.slice();
      next[idx] = { ...next[idx], ...data };
      patched = true;
      return next;
    });
    queryClient.setQueriesData({ queryKey: ['games'] }, (old: any) => {
      if (!old || typeof old !== 'object') return old;
      if (Array.isArray(old?.games)) {
        const idx = old.games.findIndex((g: any) => g?.id === update.gameId || g?.game_id === update.gameId);
        if (idx === -1) return old;
        const nextGames = old.games.slice();
        nextGames[idx] = { ...nextGames[idx], ...data };
        patched = true;
        return { ...old, games: nextGames };
      }
      return old;
    });

    // Cache miss (new row, or empty list) — fall back to a debounced refetch so we
    // don't fire N invalidations when a cron updates many games at once.
    if (!patched) {
      if (refetchDebounceRef.current) {
        clearTimeout(refetchDebounceRef.current);
      }
      refetchDebounceRef.current = setTimeout(() => {
        queryClient.invalidateQueries({ queryKey: ['home-games'] });
        queryClient.invalidateQueries({ queryKey: ['games'] });
        queryClient.invalidateQueries({ queryKey: ['allLiveScores'] });
        if (update.sport) {
          queryClient.invalidateQueries({ queryKey: ['games', update.sport] });
          queryClient.invalidateQueries({ queryKey: ['allLiveScores', update.sport] });
        }
      }, 3000);
    }
  }, [queryClient]);

  // Handle score updates (NEW)
  const handleScoreUpdate = useCallback((data: any, sport?: Sport) => {
    const update: RealTimeUpdate = {
      type: 'score_update',
      sport: sport || data.sport,
      gameId: data.id || data.game_id || data.gameId,
      data: data,
      timestamp: new Date().toISOString()
    };
    setLastUpdate(update);

    // Invalidate game-specific queries
    if (update.gameId) {
      // Sport-aware invalidation
      if (update.sport) {
        queryClient.invalidateQueries({ queryKey: ['gameScores', update.gameId, update.sport] });
      }
      queryClient.invalidateQueries({ queryKey: ['game', update.gameId] });
      queryClient.invalidateQueries({ queryKey: ['liveGame', update.gameId] });
    }
  }, [queryClient]);

  // Handle batch updates (NEW)
  const handleBatchUpdate = useCallback((data: any, sport?: Sport) => {
    console.log(`[RealTimeProvider] Processing batch update for ${sport}`, {
      games: data.count?.games,
      odds: data.count?.odds
    });

    // Invalidate all relevant queries for this sport
    if (sport) {
      queryClient.invalidateQueries({ queryKey: ['games', sport] });
      queryClient.invalidateQueries({ queryKey: ['odds', sport] });
    }
  }, [queryClient]);

  // Handle market movements
  const handleMarketMovement = useCallback((data: MarketMovement) => {
    const update: RealTimeUpdate = {
      type: 'market_movement',
      gameId: data.gameId,
      data: data,
      timestamp: new Date().toISOString()
    };
    setLastUpdate(update);

    // Only show notifications for significant movements
    if (data.significant) {
      const icon = data.direction === 'up' ? '📈' : '📉';
      const message = `${data.type} line moved ${data.direction} for game ${data.gameId}`;
      toast(message, { icon, duration: 4000 });
    }
  }, []);

  // Handle prediction updates
  const handlePredictionUpdate = useCallback((data: any) => {
    const update: RealTimeUpdate = {
      type: 'prediction_update',
      gameId: data.id || data.game_id || data.gameId,
      data: data,
      timestamp: new Date().toISOString()
    };
    setLastUpdate(update);

    // Invalidate prediction query for this game
    if (update.gameId) {
      queryClient.invalidateQueries({ queryKey: ['prediction', update.gameId] });
    }

    // Show notification for new predictions
    toast('New AI prediction available', {
      icon: '🤖'
    });
  }, [queryClient]);

  // Subscribe to a channel (Supabase Realtime filters games/odds by default)
  const subscribe = useCallback((channel: string, gameId?: string, sport?: Sport) => {
    // Supabase Realtime subscriptions are table-wide, filtering happens in handlers
    // This function maintains API compatibility but doesn't need to do anything
    // as we're already subscribed to all games and odds changes
    console.log(`[RealTimeProvider] Subscribe called for ${channel}${gameId ? `:${gameId}` : ''}${sport ? ` (${sport})` : ''}`);
    // The filtering happens in handleDatabaseChange based on gameId and sport
  }, []);

  // Unsubscribe from a channel
  const unsubscribe = useCallback((channel: string, gameId?: string, sport?: Sport) => {
    // Supabase Realtime subscriptions are table-wide
    // This function maintains API compatibility
    console.log(`[RealTimeProvider] Unsubscribe called for ${channel}${gameId ? `:${gameId}` : ''}${sport ? ` (${sport})` : ''}`);
  }, []);

  // Subscribe to all updates for a specific sport
  const subscribeToSport = useCallback((sport: Sport) => {
    // Supabase Realtime subscriptions are table-wide
    // Filtering by sport happens automatically in handlers
    console.log(`[RealTimeProvider] Subscribed to ${sport} updates (table-wide subscription active)`);

    toast.success(`Subscribed to ${sport.toUpperCase()} live updates`, {
      icon: '🏈',
      duration: 2000
    });
  }, []);

  // Unsubscribe from all updates for a specific sport
  const unsubscribeFromSport = useCallback((sport: Sport) => {
    // Supabase Realtime subscriptions are table-wide
    console.log(`[RealTimeProvider] Unsubscribed from ${sport} updates`);
  }, []);

  const contextValue: RealTimeContextType = {
    channels: channelsRef.current,
    isConnected,
    connectionStatus,
    subscribe,
    unsubscribe,
    subscribeToSport,
    unsubscribeFromSport,
    lastUpdate,
    reconnectAttempts,
  };

  return (
    <RealTimeContext.Provider value={contextValue}>
      {children}
    </RealTimeContext.Provider>
  );
};

export default RealTimeProvider;